<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <div class="d-flex justify-space-between align-center">
          <h1 class="text-h4 mb-4">{{ $t('permissions.title') }}</h1>
          <v-btn color="primary" @click="openAddDialog">{{ $t('permissions.add') }}</v-btn>
        </div>
      </v-col>
    </v-row>
    
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title>{{ $t('permissions.list') }}</v-card-title>
          <v-card-text>
            <v-table density="compact">
              <thead>
                <tr>
                  <th>{{ $t('permissions.id') }}</th>
                  <th>{{ $t('permissions.name') }}</th>
                  <th>{{ $t('permissions.controller') }}</th>
                  <th>{{ $t('permissions.action') }}</th>
                  <th>{{ $t('permissions.description') }}</th>
                  <th>{{ $t('permissions.roles') }}</th>
                  <th>{{ $t('common.actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="permission in permissions" :key="permission.id">
                  <td>{{ permission.id }}</td>
                  <td><strong>{{ permission.name }}</strong></td>
                  <td>{{ permission.controller }}</td>
                  <td>{{ permission.action || '-' }}</td>
                  <td>{{ permission.description || '-' }}</td>
                  <td>
                    <v-chip
                      v-for="role in permission.roles"
                      :key="role"
                      size="small"
                      class="mr-1"
                    >
                      {{ role }}
                    </v-chip>
                    <span v-if="permission.roles.length === 0" class="text-grey">{{ $t('permissions.noRoles') }}</span>
                  </td>
                  <td>
                    <v-btn variant="plain" size="small" @click="editPermission(permission)" icon="mdi-pencil"></v-btn>
                    <v-btn variant="plain" size="small" color="error" @click="deletePermission(permission.id)" icon="mdi-delete"></v-btn>
                  </td>
                </tr>
                <tr v-if="permissions.length === 0">
                  <td colspan="7" class="text-center text-grey">{{ $t('permissions.noPermissionsFound') }}</td>
                </tr>
              </tbody>
            </v-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="showAddDialog" max-width="600" @update:model-value="(val) => { if (!val) resetForm(); }">
      <v-card>
        <v-card-title>{{ permissionForm.id ? $t('permissions.edit') : $t('permissions.add') }}</v-card-title>
        <v-card-text>
          <v-form>
            <v-text-field
              v-model="permissionForm.name"
              :label="$t('permissions.name')"
              required
              density="compact"
              :rules="[v => !!v || $t('permissions.nameRequired')]"
            />
            <v-text-field
              v-model="permissionForm.controller"
              :label="$t('permissions.controller')"
              required
              density="compact"
              class="mt-2"
              :rules="[v => !!v || $t('permissions.controllerRequired')]"
            />
            <v-text-field
              v-model="permissionForm.action"
              :label="$t('permissions.action')"
              density="compact"
              class="mt-2"
            />
            <v-textarea
              v-model="permissionForm.description"
              :label="$t('permissions.description')"
              rows="3"
              density="compact"
              class="mt-2"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="showAddDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="primary" @click="savePermission">{{ permissionForm.id ? $t('common.save') : $t('common.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api'

const { t } = useI18n()

interface Permission {
  id: number
  name: string
  controller: string
  action: string | null
  description: string | null
  roles: string[]
}

const permissions = ref<Permission[]>([])
const showAddDialog = ref(false)
const permissionForm = ref({
  id: null as number | null,
  name: '',
  controller: '',
  action: '',
  description: ''
})

const loadPermissions = async () => {
  try {
    const response = await apiGet('/api/permissions')
    if (response.ok) {
      permissions.value = await response.json()
    }
  } catch (error) {
    console.error('Load permissions error:', error)
  }
}

const openAddDialog = () => {
  resetForm()
  showAddDialog.value = true
}

const editPermission = (permission: Permission) => {
  permissionForm.value = {
    id: permission.id,
    name: permission.name,
    controller: permission.controller,
    action: permission.action || '',
    description: permission.description || ''
  }
  showAddDialog.value = true
}

const savePermission = async () => {
  try {
    const formData = {
      permission: {
        name: permissionForm.value.name,
        controller: permissionForm.value.controller,
        action: permissionForm.value.action || null,
        description: permissionForm.value.description || null
      }
    }

    if (permissionForm.value.id) {
      const response = await apiPut(`/api/permissions/${permissionForm.value.id}`, formData)
      if (response.ok) {
        await loadPermissions()
        showAddDialog.value = false
      } else {
        const data = await response.json()
        alert(data.errors?.join(', ') || t('permissions.failedToSave'))
      }
    } else {
      const response = await apiPost('/api/permissions', formData)
      if (response.ok) {
        await loadPermissions()
        showAddDialog.value = false
      } else {
        const data = await response.json()
        alert(data.errors?.join(', ') || t('permissions.failedToCreate'))
      }
    }
  } catch (error) {
    console.error('Save permission error:', error)
    alert(t('permissions.failedToSave'))
  }
}

const deletePermission = async (id: number) => {
  if (!confirm(t('permissions.areYouSureDelete'))) return

  try {
    const response = await apiDelete(`/api/permissions/${id}`)
    if (response.ok) {
      await loadPermissions()
    } else {
      alert(t('permissions.failedToDelete'))
    }
  } catch (error) {
    console.error('Delete permission error:', error)
    alert(t('permissions.failedToDelete'))
  }
}

const resetForm = () => {
  permissionForm.value = {
    id: null,
    name: '',
    controller: '',
    action: '',
    description: ''
  }
}

onMounted(() => {
  loadPermissions()
})
</script>

